import { useState } from "react";
import { Button, ButtonVariant, CheckboxCard } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Coins01Icon,
  GraduateMaleIcon,
  Home12Icon,
  MoreHorizontalIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { IncomeType } from "shared/types";
import { CombinedIncomeLayout } from "../components/Layout";
import { useCombinedIncome } from "../context";
import { CombinedIncomeSubStep } from "../../../constants";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";

const reasonOptions = [
  {
    label: "Student",
    icon: GraduateMaleIcon,
    value: "student"
  },
  {
    label: "Home-maker",
    icon: Home12Icon,
    value: "home_maker"
  },
  {
    label: "Retiree",
    icon: Coins01Icon,
    value: "retiree"
  },
  {
    label: "Other",
    icon: MoreHorizontalIcon,
    value: "other"
  }
];

export const NoIncomeReasonStep = () => {
  const {
    updateStepMutation,
    createIncomeMutation,
    refetchVerification,
    getNextStep,
    skipHousingAssistance,
    finishVerification,
    isFinishingVerification
  } = useCombinedIncome();

  const [selectedReason, setSelectedReason] = useState<string | undefined>(
    undefined
  );

  const isLoading =
    updateStepMutation.isPending ||
    createIncomeMutation.isPending ||
    isFinishingVerification;

  const handleNext = () => {
    if (!selectedReason || isLoading) return;
    createIncomeMutation.mutate(
      {
        type: IncomeType.NoIncome,
        additional_data: {
          description: selectedReason
        }
      },
      {
        onSuccess: () => {
          const nextStep = getNextStep(CombinedIncomeSubStep.NoIncomeReason, {
            skipHousingAssistance
          });
          if (!nextStep) return;

          if (nextStep === CombinedIncomeSubStep.Completed) {
            finishVerification();
            return;
          }

          updateStepMutation.mutate(
            { lastStep: nextStep },
            {
              onSuccess: () => {
                refetchVerification();
              },
              onError: (error) => {
                toast.error(
                  getErrorMessage(error) || "Failed to submit combined income"
                );
              }
            }
          );
        }
      }
    );
  };

  return (
    <CombinedIncomeLayout
      title="What is the applicant’s current financial situation?"
      footer={
        <Button
          className="w-[132px]"
          type="button"
          loading={isLoading}
          variant={ButtonVariant.PRIMARY}
          onClick={handleNext}
          disabled={!selectedReason}
        >
          Continue
        </Button>
      }
    >
      <div className="space-y-3">
        {reasonOptions.map((option) => (
          <CheckboxCard
            key={option.value}
            checked={selectedReason === option.value}
            id={option.value}
            label={
              <div className="flex items-center gap-4">
                <div className="flex justify-center items-center bg-primary-20 w-10 h-10 rounded-lg">
                  <HugeiconsIcon
                    icon={option.icon}
                    size={24}
                    className="text-primary-500"
                  />
                </div>
                <p className="text-base text-alpha-900 font-bold">
                  {option.label}
                </p>
              </div>
            }
            name={option.value}
            wrapperClassName="w-full"
            onChange={() => {
              setSelectedReason(option.value);
            }}
          />
        ))}
      </div>
    </CombinedIncomeLayout>
  );
};
